'use client';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const modules = [
  { icon: 'ChartBarSquareIcon', name: 'Dashboard Inteligente', desc: 'Visão completa do negócio em tempo real' },
  { icon: 'ComputerDesktopIcon', name: 'PDV', desc: 'Ponto de venda rápido e intuitivo' },
  { icon: 'TableCellsIcon', name: 'Mesas', desc: 'Gestão completa de mesas e atendimento' },
  { icon: 'BuildingStorefrontIcon', name: 'Balcão', desc: 'Atendimento rápido no balcão' },
  { icon: 'FireIcon', name: 'Cozinha', desc: 'Painel de pedidos para a cozinha' },
  { icon: 'DevicePhoneMobileIcon', name: 'Cardápio Digital', desc: 'Menu digital interativo e personalizado' },
  { icon: 'QrCodeIcon', name: 'QR Code', desc: 'Pedidos via QR code na mesa' },
  { icon: 'CubeIcon', name: 'Estoque', desc: 'Controle total de entradas e saídas' },
  { icon: 'TagIcon', name: 'Produtos', desc: 'Cadastro completo com fotos e variações' },
  { icon: 'Squares2X2Icon', name: 'Categorias', desc: 'Organização inteligente por categorias' },
  { icon: 'UserGroupIcon', name: 'Clientes', desc: 'CRM completo com histórico de compras' },
  { icon: 'UsersIcon', name: 'Funcionários', desc: 'Gestão de equipe e comissões' },
  { icon: 'UserCircleIcon', name: 'Usuários', desc: 'Múltiplos usuários com controle de acesso' },
  { icon: 'KeyIcon', name: 'Permissões', desc: 'Controle granular de permissões' },
  { icon: 'BanknotesIcon', name: 'Financeiro', desc: 'Gestão financeira completa e integrada' },
  { icon: 'ArrowDownCircleIcon', name: 'Contas a Pagar', desc: 'Controle de vencimentos e pagamentos' },
  { icon: 'ArrowUpCircleIcon', name: 'Contas a Receber', desc: 'Acompanhe recebimentos e inadimplência' },
  { icon: 'ArrowsRightLeftIcon', name: 'Fluxo de Caixa', desc: 'Entradas e saídas em tempo real' },
  { icon: 'CalendarDaysIcon', name: 'Caixa Diário', desc: 'Abertura e fechamento de caixa' },
  { icon: 'DocumentChartBarIcon', name: 'Relatórios', desc: 'Relatórios detalhados e exportáveis' },
  { icon: 'PresentationChartLineIcon', name: 'Gráficos', desc: 'Visualizações interativas de dados' },
  { icon: 'TruckIcon', name: 'Fornecedores', desc: 'Gestão de fornecedores e compras' },
  { icon: 'CreditCardIcon', name: 'Assinaturas', desc: 'Gestão de contratos recorrentes' },
  { icon: 'CloudArrowUpIcon', name: 'Backup', desc: 'Backup automático e seguro na nuvem' },
  { icon: 'ClockIcon', name: 'Histórico', desc: 'Log completo de todas as operações' },
  { icon: 'CpuChipIcon', name: 'IA Integrada', desc: 'Inteligência artificial para insights' },
  { icon: 'BellIcon', name: 'Notificações', desc: 'Alertas inteligentes em tempo real' },
  { icon: 'MagnifyingGlassIcon', name: 'Auditoria', desc: 'Rastreamento completo de ações' },
  { icon: 'BuildingOffice2Icon', name: 'Multiempresa', desc: 'Gerencie várias empresas em uma conta' },
  { icon: 'UserPlusIcon', name: 'Multiusuários', desc: 'Acesso simultâneo para toda a equipe' },
  { icon: 'PuzzlePieceIcon', name: 'Integrações', desc: 'Conecte com outras ferramentas' },
  { icon: 'ChatBubbleLeftRightIcon', name: 'Suporte', desc: 'Suporte dedicado 24 horas por dia' },
];

export default function FeaturesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="funcionalidades" className="py-24 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] blob-primary opacity-20 rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-xs font-bold uppercase tracking-widest text-primary mb-6">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Funcionalidades
          </span>
          <h2 className="text-section-title font-black text-foreground mb-4">
            Tudo que sua empresa{' '}
            <span className="gradient-text">precisa</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            32 módulos integrados em uma única plataforma. Do PDV ao financeiro, do estoque à IA.
          </p>
        </div>

        {/* Grid - 32 cards, grid-cols-2 md:grid-cols-4 lg:grid-cols-4 uniform */}
        {/* BENTO AUDIT: 32 cards, all same size, grid-cols-2/4. Placed 32/32 ✓ */}
        <div ref={ref} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {modules.map((mod, i) => (
            <div
              key={mod.name}
              className={`glass-card-hover feature-card rounded-xl p-4 card-glow-effect transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${Math.min(i * 40, 800)}ms` }}
            >
              <div className="feature-icon-wrap mb-3">
                <Icon name={mod.icon as 'BellIcon'} size={18} className="text-primary" />
              </div>
              <h3 className="text-sm font-bold text-foreground mb-1">{mod.name}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{mod.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}